import { useCallback, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import queryString from 'query-string';

/**
 * A piece of UI state mirrored into a query param, so a refresh — or a shared link — restores it.
 * Same approach the list pages use for `?activeTab=` and the media galleries for `?tags=`: read the param
 * on mount, write it back with history.replace (no extra history entry), and leave every other param
 * untouched.
 *
 *   const [section, setSection] = useUrlParamState('activeTab', DEFAULT_SECTION, ALL_SECTION_KEYS);
 *
 * `allowed` (optional) whitelists values: anything else in the URL falls back to `defaultValue`, so a
 * hand-edited or stale link can't put the UI in a state it has no tab for. The param is dropped from the
 * URL while the value equals `defaultValue`, keeping the common case clean.
 */
const useUrlParamState = (key, defaultValue, allowed) => {
  const history = useHistory();
  const location = useLocation();

  const [value, setValue] = useState(() => {
    const raw = queryString.parse(location.search)[key];
    // A repeated `?k=a&k=b` parses to an array; take the first and let the whitelist judge it.
    const parsed = Array.isArray(raw) ? raw[0] : raw;
    if (parsed == null) return defaultValue;
    return !allowed || allowed.includes(parsed) ? parsed : defaultValue;
  });

  // Latest values behind refs so `set` keeps a stable identity even when callers pass an inline
  // `allowed` array — it is frequently used inside effects and callbacks.
  const latest = useRef({ location, allowed, defaultValue });
  latest.current = { location, allowed, defaultValue };

  const set = useCallback(
    (next) => {
      const { location: loc, allowed: whitelist, defaultValue: fallback } = latest.current;
      if (whitelist && !whitelist.includes(next)) return;
      setValue(next);

      const currentParams = queryString.parse(loc.search);
      const nextValue = next === fallback ? undefined : next;
      if (currentParams[key] === nextValue) return;

      const nextParams = { ...currentParams };
      if (nextValue == null) delete nextParams[key];
      else nextParams[key] = nextValue;
      history.replace({ pathname: loc.pathname, search: queryString.stringify(nextParams) });
    },
    [history, key],
  );

  return [value, set];
};

export default useUrlParamState;
